/**
 * ERGO 360 Hero Section
 * 满屏视频背景 + 打字机效果 + GSAP 进场动画
 */

class Ergo360Hero {
  constructor(container) {
    this.container = container;
    this.sectionId = container.dataset.sectionId;

    // 元素引用
    this.swiperContainer = container.querySelector('.ergo-360-hero__slides');
    this.videoWrapper = container.querySelector('.ergo-360-hero__video-wrapper');
    this.video = null; // 视频元素将由 JS 动态创建
    this.typewriterText = container.querySelector('.ergo-360-hero__typewriter-text');
    this.cursor = container.querySelector('.ergo-360-hero__cursor');
    this.icons = container.querySelectorAll('.ergo-360-hero__icon');
    this.rows = container.querySelectorAll('.ergo-360-hero__slide--ergo .ergo-360-hero__row');
    this.slides = container.querySelectorAll('.ergo-360-hero__slide');
    this.promotionVideos = container.querySelectorAll('.ergo-360-hero__slide--video video');
    this.promotionPosters = container.querySelectorAll('.ergo-360-hero__cadre-video-poster');
    this.promotionImages = container.querySelectorAll('.ergo-360-hero__cadre-media.media img[is="lazy-image"]');
    this.countdowns = container.querySelectorAll('[data-countdown-end]');
    this.dots = container.querySelectorAll('.ergo-360-hero__carousel-dot');
    this.arrows = container.querySelectorAll('.ergo-360-hero__carousel-arrow');

    // 配置
    this.words = JSON.parse(container.dataset.words || '["NECK", "SPINE", "LEGS", "HIPS"]');
    const ergoSlideIndex = Number(container.dataset.ergoSlideIndex);
    this.ergoSlideIndex = Number.isFinite(ergoSlideIndex) ? ergoSlideIndex : -1;
    this.currentWordIndex = 0;
    this.typeSpeed = 80;      // 打字速度 (ms/字符)
    this.deleteSpeed = 50;    // 删除速度 (ms/字符)
    this.blinkCount = 3;      // 光标闪烁次数
    this.blinkDuration = 500; // 每次闪烁时长 (ms)
    this.triggerTime = 2.5;   // 触发动画的视频时间点 (秒)
    this.carouselAutoplayEnabled = container.dataset.autoplayEnabled !== 'false';
    this.autoplayDelay = Number(container.dataset.autoplayDelay) || 5000;
    this.videoLoopEnabled = container.dataset.videoLoopEnabled === 'true';
    this.videoLoopDelaySeconds = Number.isFinite(Number(container.dataset.videoLoopDelay)) ? Number(container.dataset.videoLoopDelay) : 5;
    this.videoLoopDelay = this.videoLoopDelaySeconds * 1000;
    this.touchSwipeThreshold = 45;
    this.touchVerticalTolerance = 1.35;

    // 状态
    this.animationTriggered = false;
    this.typewriterActive = false;
    this.currentSlideIndex = 0;
    this.swiper = null;
    this.carouselTimer = null;
    this.videoLoopTimer = null;
    this.countdownTimer = null;
    this.dotHandlers = [];
    this.arrowHandlers = [];
    this.touchStartX = 0;
    this.touchStartY = 0;
    this.touchMoveX = 0;
    this.touchMoveY = 0;
    this.isTouchingCarousel = false;
    this.onCarouselMouseEnter = this.pauseCarousel.bind(this);
    this.onCarouselMouseLeave = this.resumeCarousel.bind(this);
    this.onCarouselTouchStart = this.handleCarouselTouchStart.bind(this);
    this.onCarouselTouchMove = this.handleCarouselTouchMove.bind(this);
    this.onCarouselTouchEnd = this.handleCarouselTouchEnd.bind(this);
    this.onVideoTimeUpdateHandler = this.onVideoTimeUpdate.bind(this);
    this.onVideoEndedHandler = this.onVideoEnded.bind(this);

    this.init();
  }

  init() {
    // 检测是否为移动端
    const isMobile = window.innerWidth < 768;

    if (isMobile) {
      // 移动端：直接触发动画，不加载视频
      this.triggerEntryAnimation();
    } else if (this.videoWrapper && this.videoWrapper.dataset.videoSrc) {
      // PC 端：动态创建视频元素
      this.createVideoElement();
    } else {
      // 无视频配置时，直接触发动画
      this.triggerEntryAnimation();
    }

    // 监听窗口大小变化
    window.addEventListener('resize', this.onResize.bind(this));

    this.syncCursorState();
    this.initPromotionPosters();
    this.initPromotionImages();
    this.initPromotionVideos();
    this.initCountdowns();
    this.syncActiveSlide(this.currentSlideIndex);
    this.startCarousel();
  }

  /**
   * 视频封面图使用主题 lazy-image 的 loading 样式，并在图片完成后移除。
   */
  initPromotionPosters() {
    this.promotionPosters.forEach((poster) => {
      const image = poster.querySelector('img');
      if (!image) {
        poster.classList.remove('loading');
        return;
      }

      const markLoaded = () => {
        image.classList.add('loaded');
        poster.classList.remove('loading');
      };

      if (image.complete) {
        markLoaded();
        return;
      }

      image.addEventListener('load', markLoaded, { once: true });
      image.addEventListener('error', markLoaded, { once: true });
    });
  }

  /**
   * 促销背景图可能在 lazy-image 升级前已加载完成，兜底移除 loading 状态。
   */
  initPromotionImages() {
    this.promotionImages.forEach((image) => {
      const media = image.closest('.ergo-360-hero__cadre-media.media');
      if (!media) return;

      const markLoaded = () => {
        image.classList.add('loaded');
        media.classList.remove('loading');
      };

      if (image.complete) {
        markLoaded();
        return;
      }

      image.addEventListener('load', markLoaded, { once: true });
      image.addEventListener('error', markLoaded, { once: true });
    });
  }

  /**
   * 视频轮播封面在首帧就绪后淡出。
   */
  initPromotionVideos() {
    const promotionVideos = this.container.querySelectorAll('.ergo-360-hero__slide--video video');

    promotionVideos.forEach((video) => {
      if (video.dataset.readyHandlerBound === 'true') return;

      const slide = video.closest('.ergo-360-hero__slide--video');
      if (!slide) return;

      const markReady = () => {
        slide.classList.add('is-video-ready');
      };

      if (video.readyState >= 2) {
        markReady();
        return;
      }

      video.dataset.readyHandlerBound = 'true';
      video.addEventListener('loadeddata', markReady, { once: true });
      video.addEventListener('canplay', markReady, { once: true });
    });
  }

  /**
   * 初始化促销倒计时。
   */
  initCountdowns() {
    if (!this.countdowns.length) return;

    this.updateCountdowns();
    this.countdownTimer = setInterval(() => {
      this.updateCountdowns();
    }, 1000);
  }

  updateCountdowns() {
    this.countdowns.forEach((countdown) => {
      // 缓存解析结果，避免每秒重复做时区换算。每个倒计时容器可通过 data-timezone 指定时区。
      if (typeof countdown._ergoEndDate === 'undefined') {
        countdown._ergoEndDate = this.parseCountdownEndDate(countdown.dataset.countdownEnd, countdown.dataset.timezone);
      }

      const endDate = countdown._ergoEndDate;

      if (!endDate || Number.isNaN(endDate.getTime())) {
        countdown.classList.add('is-invalid');
        return;
      }

      const remaining = Math.max(0, endDate.getTime() - Date.now());
      const totalSeconds = Math.floor(remaining / 1000);
      const days = Math.floor(totalSeconds / 86400);
      const hours = Math.floor((totalSeconds % 86400) / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;

      this.setCountdownPart(countdown, 'days', days);
      this.setCountdownPart(countdown, 'hours', hours);
      this.setCountdownPart(countdown, 'minutes', minutes);
      this.setCountdownPart(countdown, 'seconds', seconds);
      countdown.classList.toggle('is-expired', remaining === 0);
      countdown.classList.remove('is-invalid');
    });
  }

  setCountdownPart(countdown, part, value) {
    const node = countdown.querySelector(`[data-countdown-part="${part}"]`);
    if (!node) return;

    node.textContent = String(value).padStart(2, '0');
  }

  parseCountdownEndDate(value, timeZone) {
    if (!value) return null;

    return this.parseLocalDate(value.trim(), timeZone);
  }

  /**
   * `YYYY-MM-DD` 可跟 `HH:MM:SS`（`/` 分隔或 `T` 连接也支持）。传入 timeZone（IANA 名称，
   * 如 America/Los_Angeles）时按该时区解释挂钟时间；未传或时区无效时按访客本地时间解析。
   * 不匹配正则的值回退到原生解析。
   */
  parseLocalDate(value, timeZone) {
    const parts = this.parseDateParts(value);

    if (parts) {
      const zone = (timeZone || '').trim();

      if (zone) {
        const ms = this.zonedTimeToMs(parts, zone);

        if (ms !== null) {
          return new Date(ms);
        }
      }

      return new Date(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);
    }

    return new Date(value);
  }

  /**
   * 把挂钟时间 parts 按 timeZone（IANA 名称）解释并返回 UTC 时间戳。两次偏移换算以处理
   * 夏令时边界；浏览器不认识该时区时返回 null，调用方回退本地时间。
   */
  zonedTimeToMs(parts, timeZone) {
    const utcGuess = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);

    const zoneOffset = (date) => {
      const dtf = new Intl.DateTimeFormat('en-US', {
        timeZone,
        hour12: false,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
      const map = {};

      dtf.formatToParts(date).forEach((part) => {
        map[part.type] = part.value;
      });

      return Date.UTC(
        Number(map.year),
        Number(map.month) - 1,
        Number(map.day),
        Number(map.hour === '24' ? '0' : map.hour),
        Number(map.minute),
        Number(map.second)
      ) - date.getTime();
    };

    try {
      let ms = utcGuess - zoneOffset(new Date(utcGuess));
      ms = utcGuess - zoneOffset(new Date(ms));
      return ms;
    } catch (error) {
      return null;
    }
  }

  parseDateParts(value) {
    const match = value.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})(?:[T\s](\d{1,2})(?::(\d{1,2})(?::(\d{1,2}))?)?)?$/);
    if (!match) return null;

    return {
      year: Number(match[1]),
      month: Number(match[2]),
      day: Number(match[3]),
      hour: Number(match[4] || 0),
      minute: Number(match[5] || 0),
      second: Number(match[6] || 0)
    };
  }

  /**
   * 动态创建视频元素（仅 PC 端调用）
   * 性能优化：移动端不下载视频文件
   */
  createVideoElement() {
    const videoSrc = this.videoWrapper.dataset.videoSrc;
    if (!videoSrc) return;

    // 创建 video 元素
    const video = document.createElement('video');
    video.className = 'ergo-360-hero__video yd-absolute yd-inset-0 yd-w-full yd-h-full yd-object-cover';
    video.autoplay = true;
    video.muted = true;
    video.playsInline = true;
    video.preload = 'auto';

    // 创建 source 元素
    const source = document.createElement('source');
    source.src = videoSrc;
    source.type = 'video/mp4';
    video.appendChild(source);

    // 绑定事件监听
    video.addEventListener('timeupdate', this.onVideoTimeUpdateHandler);
    video.addEventListener('ended', this.onVideoEndedHandler);

    // 后备播放逻辑：确保跨浏览器兼容性
    video.addEventListener('loadedmetadata', () => {
      if (this.currentSlideIndex !== this.ergoSlideIndex) return;

      this.playErgoVideo(false);
    });

    // 插入 DOM
    this.videoWrapper.appendChild(video);
    this.video = video;
  }

  /**
   * 视频时间更新事件
   */
  onVideoTimeUpdate() {
    if (!this.animationTriggered && this.video.currentTime >= this.triggerTime) {
      this.triggerEntryAnimation();
    }
  }

  /**
   * 视频播放结束
   */
  onVideoEnded() {
    // 如果动画还没触发，立即触发
    if (!this.animationTriggered) {
      this.triggerEntryAnimation();
    }

    this.clearVideoLoopTimer();

    if (!this.videoLoopEnabled) return;

    this.videoLoopTimer = setTimeout(() => {
      if (this.currentSlideIndex !== this.ergoSlideIndex) return;

      this.playErgoVideo(true);
    }, this.videoLoopDelay);
  }

  /**
   * 窗口大小变化
   */
  onResize() {
    const isMobile = window.innerWidth < 768;

    // 如果切换到移动端且动画还没触发，立即触发
    if (isMobile && !this.animationTriggered) {
      this.triggerEntryAnimation();
    }
  }

  /**
   * 启动 Hero 轮播
   */
  startCarousel() {
    if (!this.slides || this.slides.length <= 1) return;

    if (typeof Swiper === 'undefined' || !this.swiperContainer) return;

    const nextArrow = this.container.querySelector('.ergo-360-hero__carousel-arrow--next');
    const previousArrow = this.container.querySelector('.ergo-360-hero__carousel-arrow--previous');
    const pagination = this.container.querySelector('.ergo-360-hero__carousel-dots');
    const swiperOptions = {
      slidesPerView: 1,
      speed: 600,
      rewind: true,
      on: {
        init: (swiper) => {
          this.syncActiveSlide(swiper.realIndex ?? swiper.activeIndex ?? 0);
          this.initPromotionVideos();
        },
        slideChange: (swiper) => {
          this.syncActiveSlide(swiper.realIndex ?? swiper.activeIndex ?? 0);
        }
      }
    };

    if (this.carouselAutoplayEnabled && this.autoplayDelay > 0) {
      swiperOptions.autoplay = {
        delay: this.autoplayDelay,
        disableOnInteraction: false
      };
    }

    if (nextArrow && previousArrow) {
      swiperOptions.navigation = {
        nextEl: nextArrow,
        prevEl: previousArrow
      };
    }

    if (pagination) {
      swiperOptions.pagination = {
        el: pagination,
        clickable: true,
        bulletClass: 'ergo-360-hero__carousel-dot',
        bulletActiveClass: 'is-active'
      };
    }

    this.swiper = new Swiper(this.swiperContainer, swiperOptions);

    this.container.addEventListener('mouseenter', this.onCarouselMouseEnter);
    this.container.addEventListener('mouseleave', this.onCarouselMouseLeave);
  }

  /**
   * 鼠标悬停时暂停自动轮播
   */
  pauseCarousel() {
    if (this.swiper && this.swiper.autoplay) {
      this.swiper.autoplay.stop();
      return;
    }

    if (!this.carouselTimer) return;

    clearInterval(this.carouselTimer);
    this.carouselTimer = null;
  }

  /**
   * 鼠标移出后恢复自动轮播
   */
  resumeCarousel() {
    if (this.swiper && this.swiper.autoplay && this.carouselAutoplayEnabled && this.autoplayDelay > 0) {
      this.swiper.autoplay.start();
      return;
    }

    this.restartCarouselTimer();
  }

  /**
   * 重置自动轮播计时器
   */
  restartCarouselTimer() {
    if (this.carouselTimer) {
      clearInterval(this.carouselTimer);
      this.carouselTimer = null;
    }

    if (!this.swiper || !this.swiper.autoplay || !this.carouselAutoplayEnabled || this.autoplayDelay <= 0) return;

    this.swiper.params.autoplay.delay = this.autoplayDelay;
    this.swiper.autoplay.start();
  }

  /**
   * 切换到指定 slide
   */
  goToSlide(index, resetTimer) {
    if (!this.slides || !this.slides[index] || index === this.currentSlideIndex) return;

    if (this.swiper) {
      this.swiper.slideTo(index);
    } else {
      this.syncActiveSlide(index);
    }

    if (resetTimer && !this.swiper) {
      this.restartCarouselTimer();
    }
  }

  /**
   * 切换到上一张 slide
   */
  goToPreviousSlide() {
    if (!this.slides || this.slides.length <= 1) return;

    if (this.swiper) {
      this.swiper.slidePrev();
      return;
    }

    const previousIndex = (this.currentSlideIndex - 1 + this.slides.length) % this.slides.length;
    this.goToSlide(previousIndex, true);
  }

  /**
   * 切换到下一张 slide
   */
  goToNextSlide() {
    if (!this.slides || this.slides.length <= 1) return;

    if (this.swiper) {
      this.swiper.slideNext();
      return;
    }

    const nextIndex = (this.currentSlideIndex + 1) % this.slides.length;
    this.goToSlide(nextIndex, true);
  }

  /**
   * 同步 Swiper 当前 slide 状态。
   */
  syncActiveSlide(index) {
    const slides = this.container.querySelectorAll('.ergo-360-hero__slide');
    if (!slides.length) return;

    let activeSlide = null;
    slides.forEach((slide) => {
      const slideIndex = Number(slide.dataset.slideIndex);
      const isActive = Number.isFinite(slideIndex) && slideIndex === index;
      slide.classList.toggle('is-active', isActive);
      if (isActive) {
        activeSlide = slide;
      }
    });

    this.currentSlideIndex = index;
    this.syncCursorState();
    this.syncCountdownLayout(activeSlide);
    this.syncTransparentHeader(activeSlide);
    this.syncVideoPlayback();
  }

  /**
   * 倒计时促销图在移动端使用独立长图布局，切换轮播时同步容器状态。
   */
  syncCountdownLayout(activeSlide) {
    this.container.classList.toggle(
      'ergo-360-hero--countdown-active',
      activeSlide?.classList.contains('ergo-360-hero__slide--countdown') === true
    );
  }

  /**
   * 当前轮播项控制透明头部，优先级高于 section 设置。
   */
  syncTransparentHeader(activeSlide) {
    if (!activeSlide || !this.isFirstPageSection()) return;

    const allowTransparentHeader = activeSlide.dataset.allowTransparentHeader === 'true';
    this.container.toggleAttribute('allow-transparent-header', allowTransparentHeader);
    this.container.classList.toggle('ergo-360-hero--header-offset', !allowTransparentHeader);

    const header = document.querySelector('header[is="sticky-header"], header[is="custom-header"]');
    if (header && typeof header.init === 'function') {
      header.init();
    }
  }

  isFirstPageSection() {
    const section = this.container.closest('.shopify-section');
    if (!section || !section.parentElement) return false;

    return section.parentElement.firstElementChild === section;
  }

  /**
   * 移动端触摸开始
   */
  handleCarouselTouchStart(event) {
    if (!event.touches || event.touches.length !== 1) return;

    const touch = event.touches[0];
    this.touchStartX = touch.clientX;
    this.touchStartY = touch.clientY;
    this.touchMoveX = touch.clientX;
    this.touchMoveY = touch.clientY;
    this.isTouchingCarousel = true;
    this.pauseCarousel();
  }

  /**
   * 移动端触摸移动，横滑时阻止页面跟随抖动，竖滑保持页面滚动
   */
  handleCarouselTouchMove(event) {
    if (!this.isTouchingCarousel || !event.touches || event.touches.length !== 1) return;

    const touch = event.touches[0];
    this.touchMoveX = touch.clientX;
    this.touchMoveY = touch.clientY;

    const deltaX = this.touchMoveX - this.touchStartX;
    const deltaY = this.touchMoveY - this.touchStartY;

    if (Math.abs(deltaX) > 12 && Math.abs(deltaX) > Math.abs(deltaY) * this.touchVerticalTolerance) {
      event.preventDefault();
    }
  }

  /**
   * 移动端触摸结束，横向滑动切换轮播
   */
  handleCarouselTouchEnd() {
    if (!this.isTouchingCarousel) return;

    const deltaX = this.touchMoveX - this.touchStartX;
    const deltaY = this.touchMoveY - this.touchStartY;
    const isHorizontalSwipe = Math.abs(deltaX) >= this.touchSwipeThreshold && Math.abs(deltaX) > Math.abs(deltaY) * this.touchVerticalTolerance;

    this.isTouchingCarousel = false;

    if (isHorizontalSwipe) {
      if (deltaX > 0) {
        this.goToPreviousSlide();
      } else {
        this.goToNextSlide();
      }
      return;
    }

    this.resumeCarousel();
  }

  /**
   * 根据当前 slide 播放或暂停第一屏视频
   */
  syncVideoPlayback() {
    if (!this.video) return;

    if (this.currentSlideIndex === this.ergoSlideIndex) {
      if (this.video.ended && !this.videoLoopEnabled) return;

      this.playErgoVideo(this.video.ended);
    } else {
      this.clearVideoLoopTimer();
      this.video.pause();
    }
  }

  /**
   * 播放 ERGO 视频。replay=true 时从头开始。
   */
  playErgoVideo(replay) {
    if (!this.video) return;

    this.clearVideoLoopTimer();

    if (replay) {
      this.video.currentTime = 0;
    }

    const playPromise = this.video.play();
    if (playPromise !== undefined) {
      playPromise.catch(() => {
        // 自动播放被阻止，保持静默
      });
    }
  }

  /**
   * 清理视频循环定时器
   */
  clearVideoLoopTimer() {
    if (!this.videoLoopTimer) return;

    clearTimeout(this.videoLoopTimer);
    this.videoLoopTimer = null;
  }

  /**
   * 第一屏整块可点击，其他促销屏只让按钮/轮播点显示手型。
   */
  syncCursorState() {
    this.container.classList.toggle('ergo-360-hero--clickable', this.ergoSlideIndex >= 0 && this.currentSlideIndex === this.ergoSlideIndex);
  }

  /**
   * 触发进场动画
   */
  triggerEntryAnimation() {
    if (this.animationTriggered) return;
    this.animationTriggered = true;

    // 检测是否为移动端
    const isMobile = window.innerWidth < 768;

    if (isMobile) {
      // 移动端：直接显示内容，跳过入场动画
      this.showContentImmediately();
      this.startTypewriter();
    } else {
      // PC 端：等待 GSAP 加载后播放入场动画
      this.waitForGSAP().then(() => {
        this.playEntryAnimation();
        this.startTypewriter();
      });
    }
  }

  /**
   * 移动端：直接显示内容（无入场动画）
   * 注意：移动端 CSS 已默认显示内容，此方法仅添加 loaded 类以保持状态一致
   */
  showContentImmediately() {
    this.container.classList.add('ergo-360-hero--loaded');
  }

  /**
   * 等待 GSAP 加载
   */
  waitForGSAP() {
    return new Promise((resolve) => {
      if (typeof gsap !== 'undefined') {
        resolve();
        return;
      }

      const checkGSAP = setInterval(() => {
        if (typeof gsap !== 'undefined') {
          clearInterval(checkGSAP);
          resolve();
        }
      }, 50);

      // 超时 3 秒后强制继续
      setTimeout(() => {
        clearInterval(checkGSAP);
        resolve();
      }, 3000);
    });
  }

  /**
   * 播放 GSAP 进场动画
   * 规则：由上至下，每下一行 offset(Y)+10, time+0.1s, delay+0.1s
   */
  playEntryAnimation() {
    if (typeof gsap === 'undefined') {
      // GSAP 未加载，直接显示
      this.container.classList.add('ergo-360-hero--loaded');
      return;
    }

    // 设置初始状态
    gsap.set(this.rows, { opacity: 0 });

    // 逐行动画
    this.rows.forEach((row, index) => {
      const yOffset = 20 + index * 10;      // Y 偏移：20, 30, 40, 50, 60...
      const duration = 0.6 + index * 0.1;   // 时长：0.6, 0.7, 0.8, 0.9, 1.0...
      const delay = index * 0.1;            // 延迟：0, 0.1, 0.2, 0.3, 0.4...

      gsap.fromTo(row,
        {
          y: yOffset,
          opacity: 0
        },
        {
          y: 0,
          opacity: 1,
          duration: duration,
          delay: delay,
          ease: 'power2.out',
          onComplete: () => {
            // 最后一行完成后添加 loaded 类
            if (index === this.rows.length - 1) {
              this.container.classList.add('ergo-360-hero--loaded');
            }
          }
        }
      );
    });
  }

  /**
   * 启动打字机效果
   */
  startTypewriter() {
    if (this.typewriterActive || !this.typewriterText || !this.cursor || !this.words.length) return;
    this.typewriterActive = true;

    this.typeNextWord();
  }

  /**
   * 打下一个词
   */
  typeNextWord() {
    const word = this.words[this.currentWordIndex];

    // 显示对应图标
    this.showIcon(word);

    // 开始打字
    this.typeText(word, () => {
      // 打字完成后，光标闪烁
      this.blinkCursor(this.blinkCount, () => {
        // 闪烁完成后，删除文字
        this.deleteText(() => {
          // 删除完成后，切换到下一个词
          this.currentWordIndex = (this.currentWordIndex + 1) % this.words.length;

          // 短暂延迟后开始下一个词
          setTimeout(() => {
            this.typeNextWord();
          }, 200);
        });
      });
    });
  }

  /**
   * 打字效果
   */
  typeText(text, callback) {
    let charIndex = 0;
    this.typewriterText.textContent = '';

    // 停止光标闪烁动画，显示静态光标
    this.cursor.classList.add('ergo-360-hero__cursor--paused');

    const typeInterval = setInterval(() => {
      if (charIndex < text.length) {
        this.typewriterText.textContent += text[charIndex];
        charIndex++;
      } else {
        clearInterval(typeInterval);
        if (callback) callback();
      }
    }, this.typeSpeed);
  }

  /**
   * 删除文字效果
   */
  deleteText(callback) {
    const text = this.typewriterText.textContent;
    let charIndex = text.length;

    // 停止光标闪烁，显示静态光标
    this.cursor.classList.add('ergo-360-hero__cursor--paused');

    const deleteInterval = setInterval(() => {
      if (charIndex > 0) {
        charIndex--;
        this.typewriterText.textContent = text.substring(0, charIndex);
      } else {
        clearInterval(deleteInterval);
        // 隐藏当前图标
        this.hideCurrentIcon();
        if (callback) callback();
      }
    }, this.deleteSpeed);
  }

  /**
   * 光标闪烁
   */
  blinkCursor(times, callback) {
    // 恢复光标闪烁动画
    this.cursor.classList.remove('ergo-360-hero__cursor--paused');

    // 等待闪烁完成
    const totalDuration = times * this.blinkDuration * 2;
    setTimeout(() => {
      if (callback) callback();
    }, totalDuration);
  }

  /**
   * 显示对应图标
   */
  showIcon(word) {
    this.icons.forEach((icon) => {
      if (icon.dataset.icon === word) {
        // 使用 GSAP 淡入（如果可用）
        if (typeof gsap !== 'undefined') {
          gsap.to(icon, {
            opacity: 1,
            duration: 0.3,
            ease: 'power2.out'
          });
        } else {
          icon.style.opacity = '1';
        }
      }
    });
  }

  /**
   * 隐藏当前图标
   */
  hideCurrentIcon() {
    const currentWord = this.words[this.currentWordIndex];
    this.icons.forEach((icon) => {
      if (icon.dataset.icon === currentWord) {
        // 使用 GSAP 淡出（如果可用）
        if (typeof gsap !== 'undefined') {
          gsap.to(icon, {
            opacity: 0,
            duration: 0.2,
            ease: 'power2.out'
          });
        } else {
          icon.style.opacity = '0';
        }
      }
    });
  }

  /**
   * 销毁实例
   */
  destroy() {
    this.typewriterActive = false;
    this.clearVideoLoopTimer();

    if (this.video) {
      this.video.removeEventListener('timeupdate', this.onVideoTimeUpdateHandler);
      this.video.removeEventListener('ended', this.onVideoEndedHandler);
      // 移除动态创建的视频元素
      this.video.remove();
      this.video = null;
    }

    if (this.carouselTimer) {
      clearInterval(this.carouselTimer);
      this.carouselTimer = null;
    }

    if (this.countdownTimer) {
      clearInterval(this.countdownTimer);
      this.countdownTimer = null;
    }

    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }

    this.dotHandlers.forEach(({ dot, handler }) => {
      dot.removeEventListener('click', handler);
    });
    this.dotHandlers = [];

    this.arrowHandlers.forEach(({ arrow, handler }) => {
      arrow.removeEventListener('click', handler);
    });
    this.arrowHandlers = [];

    this.container.removeEventListener('mouseenter', this.onCarouselMouseEnter);
    this.container.removeEventListener('mouseleave', this.onCarouselMouseLeave);
    this.container.removeEventListener('touchstart', this.onCarouselTouchStart);
    this.container.removeEventListener('touchmove', this.onCarouselTouchMove);
    this.container.removeEventListener('touchend', this.onCarouselTouchEnd);
    this.container.removeEventListener('touchcancel', this.onCarouselTouchEnd);

    window.removeEventListener('resize', this.onResize);
  }
}

// 初始化
document.addEventListener('DOMContentLoaded', () => {
  const heroContainers = document.querySelectorAll('.ergo-360-hero');

  heroContainers.forEach((container) => {
    new Ergo360Hero(container);
  });
});

// Shopify 主题编辑器支持
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const container = event.target.querySelector('.ergo-360-hero');
    if (container) {
      new Ergo360Hero(container);
    }
  });
}
